import { create } from "zustand";
import { InspectionFormData } from "../Types/InspectionTypes";

// Note: Bay management has been moved to BayStore.ts for better performance

const createEmptyForm = (): Omit<InspectionFormData, 'bays'> => ({
    customer: '',
    storage: '',
    dateServiced: new Date().toISOString().split('T')[0],
    plenumSetPoint: '',
    osaControl: '',
    osaHumidity: '',
    co2: '',
    osaTemp: '',
    systemMode: '',
    visualInspectionNotes: '',
    vfdFan: '',
    vfdFanUnit: '%',
    co2PurgeSp: '',
});

interface InspectionStore {
    formData: Omit<InspectionFormData, 'bays'>;
    savedForms: InspectionFormData[];
    availableBays: string[];
    
    // Base form actions
    updateField: <K extends keyof Omit<InspectionFormData, 'bays'>>(field: K, value: Omit<InspectionFormData, 'bays'>[K]) => void;
    
    // Bay list management (actual bay data is in BayStore)
    setAvailableBays: (bays: string[]) => void;
    
    // Form management (combines base form + bay data)
    saveForm: (baysData: InspectionFormData['bays']) => void;
    loadForm: (formId: string) => InspectionFormData['bays'] | null;
    loadInspectionData: (inspectionData: any) => void;
    resetForm: () => void;
    loadSavedForms: () => void;
    
    // Helper to get current form data with bays
    getFormDataWithBays: (baysData: InspectionFormData['bays']) => InspectionFormData;
}

export const useInspectionStore = create<InspectionStore>((set, get) => ({
    formData: createEmptyForm(), 
    savedForms: [],
    availableBays: [],
    
    updateField: (field, value) => {
        set((state) => ({
            formData: {
                ...state.formData,
                [field]: value,
            },
        }));
    },
    
    setAvailableBays: (bays) => {
        set({ availableBays: bays });
    },
    
    getFormDataWithBays: (baysData) => {
        const { formData } = get();
        return {
            ...formData,
            bays: baysData,
        };
    },
    
    saveForm: (baysData) => {
        set((state) => {
            const formToSave: InspectionFormData = {
                ...state.formData,
                bays: baysData,
                id: (state.formData as any).id || `form-${Date.now()}`,
                createdAt: (state.formData as any).createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            
            // Save to localStorage
            const existingForms = state.savedForms.filter(f => f.id !== formToSave.id);
            const updatedForms = [formToSave, ...existingForms];
            localStorage.setItem('inspectionForms', JSON.stringify(updatedForms));
            
            return {
                savedForms: updatedForms,
            };
        });
    },
    
    loadForm: (formId) => {
        const { savedForms } = get();
        const form = savedForms.find(f => f.id === formId);
        if (form) {
            // Extract bays to return them separately
            const { bays, ...formWithoutBays } = form;
            set({ formData: formWithoutBays as any });
            return bays;
        }
        return null;
    },
    
    loadInspectionData: (inspectionData) => {
        // Load inspection fields into form state with proper defaults for all fields
        set({
            formData: {
                customer: inspectionData.customer || '',
                storage: inspectionData.location?.toString() || '',
                dateServiced: inspectionData.dateServiced || new Date().toISOString().split('T')[0],
                plenumSetPoint: inspectionData.plenumSetPoint ?? '',
                osaControl: inspectionData.osaControl || '',
                osaHumidity: inspectionData.osaHumidity ?? '',
                co2: inspectionData.co2 ?? '',
                osaTemp: inspectionData.osaTemp ?? '',
                systemMode: inspectionData.systemMode || '',
                visualInspectionNotes: inspectionData.visualInspectionNotes || '',
                vfdFan: inspectionData.vfdFan ?? '',
                vfdFanUnit: inspectionData.vfdFanUnit || '%',
                co2PurgeSp: inspectionData.co2PurgeSp || '',
            }
        });
    },
    
    resetForm: () => {
        set({ formData: createEmptyForm(), availableBays: [] });
    },
    
    loadSavedForms: () => {
        const saved = localStorage.getItem('inspectionForms');
        if (saved) {
            try {
                const forms = JSON.parse(saved);
                set({ savedForms: forms });
            } catch (error) {
                console.error('Error loading saved forms:', error);
            }
        }
    },
}));

